import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { getDebitNotes } from '../../services/DebitNoteServices';
import { displayError } from '../../helpers';
import { fetchAndShowDebitNotePdf } from '../../utils/debitNotePdf';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolveCustomerFilter,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';

function DebitNoteTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    const fetchDebitNotes = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const customerIds = resolveCustomerFilter(salesSettings, customers);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !customerIds || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getDebitNotes(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
            customer_ids: customerIds,
        })
            .then((response) => {
                setData({
                    records: (response.debit_note_list || []).map((row) => ({
                        ...row,
                        status_label: row.status ? 'Active' : 'Cancelled',
                        einvoice_status_label: row.einvoice_status_label
                            || (Number(row.einvoice_status) === 4
                                ? 'IRN Cancelled'
                                : (row.einvoice_irn ? 'Generated' : 'Not Generated')),
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load Debit Notes.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, customers, plants, salesSettings]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'dn_no', headerName: 'Debit Note No' },
        { fieldName: 'dn_date', headerName: 'Debit Note Date' },
        { fieldName: 'customer_name', headerName: 'Customer Name' },
        { fieldName: 'site_name', headerName: 'Site Name' },
        { fieldName: 'dn_type_label', headerName: 'Debit Note Type' },
        { fieldName: 'voucher_mode', headerName: 'Voucher Mode' },
        { fieldName: 'invoice_no', headerName: 'Invoice No' },
        { fieldName: 'debit_amt', headerName: 'Amount' },
        { fieldName: 'einvoice_irn', headerName: 'E-Debit Note IRN' },
        { fieldName: 'einvoice_status_label', headerName: 'E-Debit Note Status' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return data.records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(() => {
        const activeCount = filteredRecords.filter((row) => row.status).length;
        const totalAmount = filteredRecords.reduce((sum, row) => sum + (parseFloat(row.debit_amt) || 0), 0);
        return [
            { label: 'ACTIVE', value: String(activeCount), accent: 'b2b' },
            { label: 'TOTAL AMOUNT', value: totalAmount.toFixed(2), accent: 'b2c' },
        ];
    }, [filteredRecords]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, customers),
        [salesSettings, plants, customers],
    );

    const onEditDebitNote = (DebitNoteId) => {
        navigate(`/DebitNoteEdit/${DebitNoteId}/`);
    };

    const onPrintDebitNote = (DebitNoteId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchAndShowDebitNotePdf(cookies, DebitNoteId, { title: 'Debit Note PDF' })
            .finally(() => setIsLoading(false));
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'DebitNote',
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Debit Note',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords,
            'DebitNote.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        saveSalesListSettings(cookies.myUserId, nextSettings);
        setSalesSettings(nextSettings);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        Promise.all([getUserAllowedPlants(cookies), getAllCustomerMain(cookies)])
            .then(([plantList, customerList]) => {
                setPlants((plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
                setCustomers((customerList.customer_list || [])
                    .filter((customer) => customer.status)
                    .map((customer) => ({ value: customer.id, label: customer.name })));
                setIsInitialized(true);
            })
            .catch(() => setIsLoading(false));
    }, [cookies]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchDebitNotes();
    }, [isInitialized, plants.length, customers.length, salesSettings, activeDateRange, fetchDebitNotes]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="List of Debit Note"
                    subtitle="Review Debit Notes for sales return, rate difference, discount, and other invoice adjustments."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="Debit Notes"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/DebitNote"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of Debit Note"
                                columns={columns}
                                data={filteredRecords}
                                onRowEdit={onEditDebitNote}
                                onRowPrint={onPrintDebitNote}
                                hideDelete
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                hidePrint={false}
                                autoPinActionColumn={false}
                                compactRows
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={customers}
                    initialSettings={salesSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default DebitNoteTable;
